/**
 * Canonical OwnYou demographic keys for the disclosure wire (Slice 0 / AT-01).
 *
 * Single source of truth shared by:
 *   - profileTierFormatter (formats tiered demographics + household groups)
 *   - The wire-builder in apps/consumer/src/services/extension-credential-sync.ts
 *     (writes records with section: 'demographics' | 'household' into
 *     `claimSet.demographics`)
 *   - The G3 alignment unit test in @ownyou/bbs-plus
 *     (asserts every value here is present in
 *     `disclosure-policy-v2.json :: canonical_demographics`)
 *
 * Per Slice 0 KAD-0.5 the demographics-half and household-half merge into
 * one map: household-section records route into `claimSet.demographics` on
 * the wire (no separate household claim family). Both halves were previously
 * inline in profileTierFormatter.ts:117-124,184-198; this module is the
 * extraction.
 *
 * The keys (left-hand side) are IAB Audience Taxonomy 1.1 `grouping_value`
 * strings as produced by the classifier / taxonomy loader. The values
 * (right-hand side) are the canonical OwnYou demographic keys written into
 * `claimSet.demographics`.
 */
/**
 * Map from IAB Audience Taxonomy 1.1 `grouping_value` strings to canonical
 * OwnYou demographic keys.
 *
 * Demographics half (Gender, Age, Education, Employment Status, Marital
 * Status, Language) was previously at profileTierFormatter.ts:117-124.
 * Household half (Home Location, Household Income (USD), Length of Residence,
 * Life Stage, Median Home Value (USD), Monthly Housing Payment (USD), Number
 * of Adults, Number of Children, Number of Individuals, Ownership, Property
 * Type, Urbanization, Language) was at profileTierFormatter.ts:184-198.
 *
 * `Language` appears in both halves and resolves to the same canonical
 * key (`language`); the merged map carries one entry.
 */
export const groupingToField = {
    // Demographics half (preserved verbatim from profileTierFormatter.ts:117-124)
    Gender: 'gender',
    Age: 'age_range',
    'Education (Highest Level)': 'education',
    'Employment Status': 'occupation',
    'Marital Status': 'marital_status',
    Language: 'language',
    // Household half (preserved verbatim from profileTierFormatter.ts:184-198;
    // KAD-0.5: merges into the same map, no separate household claim family)
    'Home Location': 'location',
    'Household Income (USD)': 'income',
    'Length of Residence': 'length_of_residence',
    'Life Stage': 'life_stage',
    'Median Home Value (USD)': 'median_home_value',
    'Monthly Housing Payment (USD)': 'monthly_housing_payment',
    'Number of Adults': 'number_of_adults',
    'Number of Children': 'number_of_children',
    'Number of Individuals': 'number_of_individuals',
    Ownership: 'ownership',
    'Property Type': 'property_type',
    Urbanization: 'urbanization',
};
/**
 * The 18 canonical OwnYou demographic keys — exactly the set of distinct
 * values in `groupingToField`.
 *
 * AT-02's G3 alignment test asserts that
 * `disclosure-policy-v2.json :: canonical_demographics` is a permutation of
 * this array; AT-06's wire-builder filters demographic records against the
 * loaded policy's `canonical_demographics` whitelist (which derives from
 * this set).
 */
export const CANONICAL_DEMOGRAPHIC_KEYS = Array.from(new Set(Object.values(groupingToField)));
//# sourceMappingURL=canonical-demographic-keys.js.map